import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Helmet } from 'react-helmet-async';
import { API_URL } from '../../utils/api';

const AdminReferrals = () => {
  const [referredUsers, setReferredUsers] = useState([]);
  const [usersWithReferrals, setUsersWithReferrals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/referral/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferredUsers(response.data.referredUsers || []);
      setUsersWithReferrals(response.data.usersWithReferrals || []);
    } catch (error) {
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet><title>Referral Management - Admin</title></Helmet>
      <div className="bg-gray-50 min-h-screen py-8">
        <div className="container mx-auto px-4">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800">Referral Management</h1>
            <p className="text-gray-600 mt-1">Track all referral relationships</p>
          </div>

          {/* Referred Users Table */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden mb-8">
            <div className="px-6 py-4 border-b bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-800">Users Who Joined via Referral</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referred User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referrer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referrer Code</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Invested</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {referredUsers.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                        No referral joins yet
                      </td>
                    </tr>
                  ) : (
                    referredUsers.map((user, idx) => (
                      <tr key={idx}>
                        <td className="px-6 py-4">
                          <div className="font-medium">{user.name}</div>
                          <div className="text-xs text-gray-500">{user.email}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-medium">{user.referredBy?.name || 'N/A'}</div>
                          <div className="text-xs text-gray-500">{user.referredBy?.email}</div>
                        </td>
                        <td className="px-6 py-4 font-mono text-sm">{user.referredBy?.referralCode || 'N/A'}</td>
                        <td className="px-6 py-4 text-sm">{new Date(user.createdAt).toLocaleDateString()}</td>
                        <td className="px-6 py-4 font-semibold text-green-600">₹{user.totalInvested?.toLocaleString() || 0}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Users with Referrals Table */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="px-6 py-4 border-b bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-800">Users Who Referred Others</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referrer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referral Code</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Referrals</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Earnings</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referred Users</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {usersWithReferrals.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                        No referrals made yet
                      </td>
                    </tr>
                  ) : (
                    usersWithReferrals.map((user, idx) => (
                      <tr key={idx}>
                        <td className="px-6 py-4">
                          <div className="font-medium">{user.name}</div>
                          <div className="text-xs text-gray-500">{user.email}</div>
                        </td>
                        <td className="px-6 py-4 font-mono text-sm">{user.referralCode}</td>
                        <td className="px-6 py-4 font-semibold">{user.referrals?.length || 0}</td>
                        <td className="px-6 py-4 font-bold text-purple-600">₹{user.totalReferralEarnings ? user.totalReferralEarnings.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '0'}</td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            {user.referrals?.map((ref, i) => (
                              <div key={i} className="text-sm">
                                {ref.user?.name} (₹{ref.totalInvestment?.toLocaleString() || 0})
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminReferrals;